import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client, urlFor } from '../sanityClient';
import SEO from '../components/SEO';
import './Home.css';

function Home() {
  const [home, setHome] = useState(null);
  const [about, setAbout] = useState(null);
  const [moments, setMoments] = useState([]);

  useEffect(() => {
    client.fetch('*[_type == "home"][0]').then(data => {
      if (data) setHome(data);
    });
    client.fetch('*[_type == "about"][0]').then(data => {
      if (data) setAbout(data);
    });
    client.fetch('*[_type == "moments"] | order(order asc)').then(data => {
      setMoments(data);
    });
  }, []);

  const heroTitle = (home && home.heroTitle) || 'A Little Wedding,\nLike a Fairytale House';
  const heroSubtitle = (home && home.heroSubtitle) || '동화 같은 집에서 하는 작은 결혼식\n나와 당신, 그리고 가장 사랑하는 사람들만.\n그걸로 충분한 하루.';
  const brandImage = home && home.brandImage;
  const heroTagline = (home && home.heroTagline) || '2019년부터 이어온 Garden Poète의 공간입니다.';

  // Brand Story data from About
  const storyParagraphs = (about && about.storyParagraphs) || [];
  const transitionHeading = (about && about.transitionHeading) || "";
  const visionParagraphs = (about && about.visionParagraphs) || [];
  const visionClosing = (about && about.visionClosing) || '';
  const philosophyHeading = (about && about.philosophyHeading) || '';
  const philosophyParagraphs = (about && about.philosophyParagraphs) || [];

  const defaultSpaces = [
    { name: '정원 Garden', link: '/venue/garden', description: '페어리 라이트가 흔들리는 자작나무 정원', order: 1 },
    { name: '홀 Hall', link: '/venue/hall', description: '하얀 벽돌 벽 아래, 친밀한 공간', order: 2 },
    { name: '다이닝 Dining', link: '/venue/house', description: '햇살이 들어오는 창가, 웃음이 모이는 테이블', order: 3 },
  ];
  const spacesPreview = (home && home.spacesPreview && home.spacesPreview.length > 0)
    ? home.spacesPreview.sort(function(a, b) { return (a.order || 0) - (b.order || 0); })
    : defaultSpaces;

  const ctaTitle = (home && home.ctaTitle) || 'Small weddings. Big memories.';
  const ctaDescription = (home && home.ctaDescription) || '작은 결혼식이 더 오래 남습니다.\n당신의 이야기를 시작하세요.';
  const ctaButtonText = (home && home.ctaButtonText) || '문의하기';
  const ctaButtonLink = (home && home.ctaButtonLink) || '/custom';

  return (
    <div className="home">
      <SEO
        title="홍대 하우스웨딩 | 동화 같은 스몰웨딩"
        description="가든포에트 홍대 - 상수역 4분, 300평 유럽풍 정원에서 만드는 동화 같은 하우스웨딩. 40~80명 소규모 웨딩, 정원 예식, 프라이빗 파티."
        path="/"
      />
      <section className="hero">
        <div className="hero-content fade-in">
          <h1>
            {heroTitle.split('\n').map(function(line, i) {
              return <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>;
            })}
          </h1>
          <p className="hero-subtitle">
            {heroSubtitle.split('\n').map(function(line, i) {
              return <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>;
            })}
          </p>
          
          {brandImage && (
            <div className="hero-brand-image">
              <img 
                src={urlFor(brandImage).width(600).url()} 
                alt="Garden Poète"
              />
            </div>
          )}
          
          <div className="hero-tagline">{heroTagline}</div>
        </div>
      </section>

      {/* Brand Story Section */}
      {(storyParagraphs.length > 0 || visionParagraphs.length > 0 || philosophyParagraphs.length > 0) && (
        <section className="brand-story">
          <div className="container">
            <div className="brand-story-content fade-in">
              {storyParagraphs.length > 0 && (
                <div className="story-section">
                  {storyParagraphs.map(function(p, i) {
                    return (
                      <p key={i} className="story-paragraph">
                        {p.split('\n').map(function(line, j) {
                          return <React.Fragment key={j}>{j > 0 && <br />}{line}</React.Fragment>;
                        })}
                      </p>
                    );
                  })}
                </div>
              )}

              {transitionHeading && (
                <div className="transition-section">
                  <h2>{transitionHeading}</h2>
                </div>
              )}

              {visionParagraphs.length > 0 && (
                <div className="vision-section">
                  {visionParagraphs.map(function(p, i) {
                    return (
                      <p key={i} className="vision-paragraph">
                        {p.split('\n').map(function(line, j) {
                          return <React.Fragment key={j}>{j > 0 && <br />}{line}</React.Fragment>;
                        })}
                      </p>
                    );
                  })}
                  {visionClosing && (
                    <p className="vision-closing">
                      {visionClosing.split('\n').map(function(line, i) {
                        return <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>;
                      })}
                    </p>
                  )}
                </div>
              )}

              {philosophyParagraphs.length > 0 && (
                <div className="philosophy-section">
                  {philosophyHeading && <h3>{philosophyHeading}</h3>}
                  {philosophyParagraphs.map(function(p, i) {
                    return (
                      <p key={i}>
                        {p.split('\n').map(function(line, j) {
                          return <React.Fragment key={j}>{j > 0 && <br />}{line}</React.Fragment>;
                        })}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Moments Section */}
      {moments.length > 0 && (
        <section className="moments-gallery">
          <div className="container-wide">
            <div className="moments-grid">
              {moments.map(function(moment) {
                return (
                  <div key={moment._id} className="moment-card fade-in">
                    {moment.image && (
                      <img src={urlFor(moment.image).width(600).url()} alt={moment.caption || 'Garden Poète moment'} />
                    )}
                    {moment.caption && <p className="moment-caption">{moment.caption}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="spaces-preview">
        <div className="container-wide">
          <div className="spaces-grid">
            {spacesPreview.map(function(space, i) {
              return (
                <Link key={i} to={space.link} className="space-card fade-in">
                  <div className="space-image">
                    {space.image ? (
                      <img src={urlFor(space.image).width(600).url()} alt={space.name} />
                    ) : (
                      <div className="space-placeholder">{space.name}</div>
                    )}
                  </div>
                  <div className="space-info">
                    <h3>{space.name}</h3>
                    <p>{space.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="container">
          <div className="cta-content fade-in">
            <h2>{ctaTitle}</h2>
            <p className="cta-description">
              {ctaDescription.split('\n').map(function(line, i) {
                return <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>;
              })}
            </p>
            <Link to={ctaButtonLink} className="cta-button">{ctaButtonText}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
